import api from './Api'

export enum EProgram {
    id = 'id',
    isDisplay = 'isDisplay',
    displayIdx = 'displayIdx',
    title = 'title',
    description = 'description',
    isBooking = 'isBooking',
    isPaid = 'isPaid',
    isAutoApprovalEnabled = 'isAutoApprovalEnabled',
    isOnline = 'isOnline',
    onlineUrl = 'onlineUrl',
    roadNameAddress = 'roadNameAddress',
    lat = 'lat',
    lon = 'lon',
    locationName = 'locationName',
    thumbnailImageUrl = 'thumbnailImageUrl',
    detailImageUrlList = 'detailImageUrlList',
    detailHtml = 'detailHtml',
    notificationHtml = 'notificationHtml',
    refundNotiHtml = 'refundNotiHtml',
    programPeriod = 'programPeriod',
    bookingPeriod = 'bookingPeriod',
    createDateTime = 'createDateTime',
    bookingCnt = 'bookingCnt',
    programEventOutlineList = 'programEventOutlineList',
    programTicketList = `programTicketList`,
    programSelectOptionList = 'programSelectOptionList',
    programSubjectOptionList = 'programSubjectOptionList',
}

export enum EProgramEventOutline {
    id = 'id',
    day = 'day',
    startTime = 'startTime',
    programEventList = 'programEventList',
}

export enum EProgramEvent {
    id = 'id',
    targetDateTime = 'targetDateTime',
    maxBookingCnt = 'maxBookingCnt',
    isActive = 'isActive',
}

export enum EProgramTicket {
    id = 'id',
    name = 'name',
    description = 'description',
    payAmount = 'payAmount',
}

export enum EProgramSelectOption {
    id = 'id',
    idx = 'idx',
    name = 'name',
    isRequired = 'isRequired',
    description = 'description',
    programSelectOptionItemList = 'programSelectOptionItemList',
}
export enum EProgramSelectOptionItem {
    id = 'id',
    name = 'name',
}

export enum EProgramSubjectOption {
    id = 'id',
    idx = 'idx',
    name = 'name',
    description = 'description',
    isRequired = 'isRequired',
}

const defaultFieldListMap: IProgramField = {
    program: [EProgram.id, EProgram.isDisplay, EProgram.title, EProgram.description, EProgram.isBooking, EProgram.isPaid, EProgram.isAutoApprovalEnabled, EProgram.isOnline, EProgram.onlineUrl, EProgram.roadNameAddress, EProgram.lat, EProgram.lon, EProgram.locationName, EProgram.thumbnailImageUrl, EProgram.detailImageUrlList, EProgram.detailHtml, EProgram.notificationHtml, EProgram.refundNotiHtml, EProgram.programPeriod, EProgram.bookingPeriod, EProgram.createDateTime, EProgram.bookingCnt, EProgram.programEventOutlineList, EProgram.programTicketList, EProgram.programSelectOptionList, EProgram.programSubjectOptionList],
    programEventOutlineList: [EProgramEventOutline.id, EProgramEventOutline.day, EProgramEventOutline.startTime, EProgramEventOutline.programEventList],
    [EProgramEventOutline.programEventList]: [EProgramEvent.id, EProgramEvent.targetDateTime, EProgramEvent.maxBookingCnt, EProgramEvent.isActive],
    programTicketList: [EProgramTicket.id, EProgramTicket.name, EProgramTicket.description, EProgramTicket.payAmount],
    programSelectOptionList: [EProgramSelectOption.id, EProgramSelectOption.idx, EProgramSelectOption.name, EProgramSelectOption.isRequired, EProgramSelectOption.description, EProgramSelectOption.programSelectOptionItemList],
    [EProgramSelectOption.programSelectOptionItemList]: [EProgramSelectOptionItem.id, EProgramSelectOptionItem.name],
    programSubjectOptionList: [EProgramSubjectOption.id, EProgramSubjectOption.idx, EProgramSubjectOption.name, EProgramSubjectOption.description, EProgramSubjectOption.isRequired],
}

const transformData = (inputFields: IProgramField) => {
    const fieldListMap: Record<string, string[]> = {};
    let queryFieldListMap: string = '';
    const keys = Object.keys(inputFields)

    Object.entries(inputFields).forEach(([key, values]) => {
        const filteredValues = values.filter((value: string) => !keys.includes(value));
        if (filteredValues.length > 0) {
            fieldListMap[key] = filteredValues;
        }
        queryFieldListMap.includes(key)
            ? queryFieldListMap = queryFieldListMap.replace(key, `${key} { ${values.join(' ')} }`)
            : queryFieldListMap += values.join(' ')
    });
    return { queryFieldListMap, fieldListMap };
};

export const getProgramById = (id: number, programField: IProgramField = defaultFieldListMap) => {
    const { queryFieldListMap, fieldListMap } = transformData(programField)
    const query = `query GetProgramById($id:ID!, $fieldListMap: JSON) { getProgramById(id:$id, fieldListMap:$fieldListMap) { ${queryFieldListMap} } }`
    return api.post<IGraphqlResponse<{ getProgramById: IProgramContainer }>>(`/graphql`, { query, variables: { id, fieldListMap } })
};


export const getProgramList = <T>(programField: IProgramField = defaultFieldListMap) => {
    const { queryFieldListMap, fieldListMap } = transformData(programField)
    const query = `query GetProgramList($fieldListMap: JSON) { getProgramList(fieldListMap:$fieldListMap) { ${queryFieldListMap} } }`
    return api.post<IGraphqlResponse<{ getProgramList: T }>>(`/graphql`, { query, variables: { fieldListMap } })
};

export const createProgramByCreateProgramAdminRequest = (createProgramAdminRequest: ICreateProgramAdminRequest) => {
    const formData = new FormData();

    const operations = {
        query: `mutation CreateProgramByProgramAdminCreateRequest($createProgramAdminRequest: CreateProgramAdminRequest) {createProgramByCreateProgramAdminRequest(createProgramAdminRequest: $createProgramAdminRequest) }`,
        variables: {
            createProgramAdminRequest: {
                ...createProgramAdminRequest,
                thumbnailImageFile: null,
                detailImageFileList: createProgramAdminRequest.detailImageFileList.map(() => null)
            }
        },
    };

    const map = {
        "thumbnailImageFile": ["variables.createProgramAdminRequest.thumbnailImageFile"],
        ...Object.fromEntries(createProgramAdminRequest.detailImageFileList.map((_, index) => [`detailImageFileList[${index}]`, [`variables.createProgramAdminRequest.detailImageFileList.${index}`]])),
    };

    formData.append("operations", JSON.stringify(operations));
    formData.append("map", JSON.stringify(map));
    formData.append("thumbnailImageFile", createProgramAdminRequest.thumbnailImageFile);
    createProgramAdminRequest.detailImageFileList.forEach((detailImageFile, index) => formData.append(`detailImageFileList[${index}]`, detailImageFile));

    return api.post<IGraphqlResponse<{ createProgramByProgramAdminCreateRequest: boolean }>>(`/graphql`, formData, { headers: { 'Content-Type': 'multipart/form-data', "apollo-require-preflight": "true", } })
};


export const updateProgramByUpdateProgramAdminRequest = (updateProgramAdminRequest: IUpdateProgramAdminRequest) => {
    const formData = new FormData();

    const operations = JSON.stringify({
        query: `mutation UpdateProgramByUpdateProgramAdminRequest($updateProgramAdminRequest: UpdateProgramAdminRequest) {updateProgramByUpdateProgramAdminRequest(updateProgramAdminRequest: $updateProgramAdminRequest) }`,
        variables: {
            updateProgramAdminRequest: {
                ...updateProgramAdminRequest,
                thumbnailImageFile: null,
                detailImageFileList: updateProgramAdminRequest.detailImageFileList?.map(() => null) ?? null
            }
        },
    });

    const map = JSON.stringify({
        "thumbnailImageFile": ["variables.updateProgramAdminRequest.thumbnailImageFile"],
        ...Object.fromEntries(
            updateProgramAdminRequest.detailImageFileList?.map((_, index) => [
                `detailImageFileList[${index}]`,
                [`variables.updateProgramAdminRequest.detailImageFileList.${index}`]
            ]) ?? []
        ),
    });

    formData.append("operations", operations);
    formData.append("map", map);
    formData.append("thumbnailImageFile", updateProgramAdminRequest.thumbnailImageFile);
    updateProgramAdminRequest.detailImageFileList?.forEach((file, index) => formData.append(`detailImageFileList[${index}]`, file));


    return api.post<IGraphqlResponse<{ updateProgramByUpdateProgramAdminRequest: boolean }>>(`/graphql`, formData, { headers: { 'Content-Type': 'multipart/form-data', "apollo-require-preflight": "true", } })
};







export const getProgramForApplyListByProgramId = (programId: number) => {
    return api.get<IProgramForApplyList>(`${process.env.REACT_APP_API_DOMAIN}/wellness-program/program/${programId}/apply/v2`)
}

export const getProgramApplyDetailForUpdateById = (id: number) => {
    return api.get<IProgramApplyDetailForUpdate>(`${process.env.REACT_APP_API_DOMAIN}/wellness-program/program/apply/v2/${id}`)
}

export const updateProgramApply = (programApplyDetailForUpdate: IProgramApplyDetailForUpdate) => {
    return api.put<boolean>(`${process.env.REACT_APP_API_DOMAIN}/wellness-program/program/apply/v2`, programApplyDetailForUpdate)
}

export const updateStatusApplyList = (updateStatusApplyListRequest: { idList: number[], selectedStatusId: number }) => {
    return api.put<boolean>(`${process.env.REACT_APP_API_DOMAIN}/wellness-program/program/apply/status/v2`, updateStatusApplyListRequest)
}

export const updateProgramDisplayIdxByUpdateProgramAdminRequest = (updateProgramAdminRequest: { id: number, displayIdx: number }) => {
    return api.patch<boolean>(`${process.env.REACT_APP_API_DOMAIN}/program/admin/display-idx`, updateProgramAdminRequest)
}